import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCartPlus,
  FaCheckCircle,
  FaDownload,
  FaImages,
  FaLock,
  FaShoppingCart,
  FaTrash,
  FaUpload,
} from 'react-icons/fa';
import { getMyPhotos, getPhotoDisplayUrl, getFlaskUploadUrl, downloadPhoto, removeMyPhoto } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import './MyPhotos.css';

const getPhotoDisplayName = (photo) => {
  const name = photo?.name || photo?.title || photo?.filename || `Photo ${photo?.id || ''}`;
  return name.split('/').pop() || name;
};

/** Free photos are downloadable by anyone; priced ones only once bought. */
const isFree = (photo) => Number(photo?.price || 0) <= 0;

const canDownload = (photo) => isFree(photo) || Boolean(photo?.has_purchased);

/** Only a priced photo the viewer has not bought needs to go through the cart. */
const needsPurchase = (photo) => !isFree(photo) && !photo?.has_purchased;

function MyPhotos() {
  const { user } = useAuth();
  const { items: cartItems, toggleItem } = useCart();
  const [photoGroups, setPhotoGroups] = useState([]);
  const [totals, setTotals] = useState({ photos: 0, events: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const [downloadingPhotoId, setDownloadingPhotoId] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await getMyPhotos();
      setPhotoGroups(response.data);
      setTotals({ photos: response.total_photos, events: response.total_events });
    } catch (err) {
      setError(err.message || 'Impossible de charger les photos');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (photo) => {
    setDownloadingPhotoId(photo.id);
    setMessage({ type: '', text: '' });

    try {
      const blob = await downloadPhoto(photo.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = getPhotoDisplayName(photo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.message || 'Le téléchargement a échoué.',
      });
    } finally {
      setDownloadingPhotoId(null);
    }
  };

  const handleRemovePhoto = async (photoId) => {
    if (!window.confirm('Retirer cette photo de vos photos découvertes ?')) return;

    setDeletingPhotoId(photoId);
    setMessage({ type: '', text: '' });

    try {
      await removeMyPhoto(photoId);

      setPhotoGroups(prev => {
        const nextGroups = prev
          .map(group => ({
            ...group,
            photos: group.photos.filter(photo => photo.id !== photoId),
          }))
          .filter(group => group.photos.length > 0);

        setTotals({
          photos: nextGroups.reduce((sum, group) => sum + group.photos.length, 0),
          events: nextGroups.length,
        });

        return nextGroups;
      });

      setMessage({ type: 'success', text: 'Photo retirée de vos photos découvertes.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Impossible de retirer la photo' });
    } finally {
      setDeletingPhotoId(null);
    }
  };

  if (loading) {
    return (
      <div className="my-photos">
        <div className="container my-photos-state">
          <div className="spinner"></div>
          <p>Chargement de vos photos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-photos">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!user?.profile_photo) {
    return (
      <div className="my-photos">
        <div className="container my-photos-state">
          <FaUpload style={{ color: 'var(--primary-orange)' }} />
          <h2>Aucune photo de profil pour le moment</h2>
          <p>
            Importez votre photo de profil pour commencer à découvrir les photos
            où vous apparaissez !
          </p>
          <Link to="/profile-upload" className="btn-primary">
            Importer une photo de profil
          </Link>
        </div>
      </div>
    );
  }

  if (photoGroups.length === 0) {
    return (
      <div className="my-photos">
        <div className="container my-photos-state">
          <FaImages style={{ color: 'var(--gray)' }} />
          <h2>Aucune correspondance trouvée pour le moment</h2>
          <p>
            Nous n'avons pas encore trouvé de photos correspondant à votre profil.
            Revenez plus tard lorsque les photographes auront importé d'autres
            photos d'événements !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-photos">
      <div className="container">
        <div className="my-photos-header">
          <div>
            <h1>Vos photos découvertes</h1>
            <p>
              {totals.photos} photo{totals.photos > 1 ? 's' : ''} trouvée{totals.photos > 1 ? 's' : ''}
              {' '}dans {totals.events} événement{totals.events > 1 ? 's' : ''}
            </p>
          </div>

          {cartItems.length > 0 && (
            <Link to="/checkout" className="my-photos-cart-link">
              <FaShoppingCart />
              Voir le panier ({cartItems.length})
            </Link>
          )}
        </div>

        {message.text && (
          <div
            className={message.type === 'error' ? 'error-message' : 'success-message'}
            style={{ marginBottom: '1.5rem' }}
          >
            {message.text}
          </div>
        )}

        {photoGroups.map((group) => (
          <section key={group.event_id} className="my-photos-event">
            <div className="my-photos-event-header">
              <h3>{group.event_name}</h3>
              <div className="my-photos-event-meta">
                <span className="category-badge">{group.event_category}</span>
                {group.event_date && (
                  <span>📅 {new Date(group.event_date).toLocaleDateString('fr-FR')}</span>
                )}
                <span>📸 {group.photos.length} photo{group.photos.length > 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="my-photos-grid">
              {group.photos.map((photo) => {
                const downloadable = canDownload(photo);
                const mustBuy = needsPurchase(photo);
                const free = isFree(photo);
                const inCart = cartItems.some(item => item.id === photo.id);
                const score = Number(photo.confidence_score);

                return (
                  <article key={photo.id} className="my-photo-card">
                    <div className="my-photo-thumb">
                      <img
                        src={getPhotoDisplayUrl(photo)}
                        alt={`Correspondance depuis ${group.event_name}`}
                        loading="lazy"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onError={(e) => {
                          // Never fall back to the unwatermarked original for a
                          // photo the viewer is not entitled to.
                          if (e.currentTarget.dataset.fallbackUsed || !downloadable) {
                            e.currentTarget.src = 'https://placehold.co/300x225/png';
                            return;
                          }

                          e.currentTarget.dataset.fallbackUsed = 'true';
                          e.currentTarget.src = getFlaskUploadUrl(photo.filename);
                        }}
                      />
                      <span className={`my-photo-badge ${downloadable ? 'owned' : 'locked'}`}>
                        {free
                          ? <><FaCheckCircle /> Gratuite</>
                          : downloadable
                            ? <><FaCheckCircle /> Achetée</>
                            : <><FaLock /> Aperçu</>}
                      </span>
                    </div>

                    <div className="my-photo-body">
                      <div className="my-photo-name" title={getPhotoDisplayName(photo)}>
                        {getPhotoDisplayName(photo)}
                      </div>

                      {Number.isFinite(score) && (
                        <>
                          <div className="my-photo-match">
                            <span>Correspondance</span>
                            <strong>{score.toFixed(0)}%</strong>
                          </div>
                          <div className="confidence-track">
                            <div className="confidence-fill" style={{ width: `${Math.min(100, score)}%` }} />
                          </div>
                        </>
                      )}

                      {photo.uploaded_at && (
                        <div className="my-photo-date">
                          Importée le {new Date(photo.uploaded_at).toLocaleDateString('fr-FR')}
                        </div>
                      )}

                      {mustBuy && (
                        <div className="my-photo-price">{Number(photo.price).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F</div>
                      )}

                      <div className="my-photo-actions">
                        {downloadable && (
                          <button
                            type="button"
                            className="my-photo-btn download"
                            onClick={() => handleDownload(photo)}
                            disabled={downloadingPhotoId === photo.id}
                          >
                            <FaDownload />
                            {downloadingPhotoId === photo.id ? 'Téléchargement...' : 'Télécharger'}
                          </button>
                        )}

                        {mustBuy && (
                          <>
                            <button
                              type="button"
                              className={`my-photo-btn cart ${inCart ? 'in-cart' : ''}`}
                              onClick={() => toggleItem(photo, group.event_id)}
                            >
                              {inCart ? <><FaShoppingCart /> Dans le panier</> : <><FaCartPlus /> Ajouter au panier</>}
                            </button>
                            <p className="my-photo-hint">
                              Achetez cette photo pour la télécharger sans filigrane.
                            </p>
                          </>
                        )}

                        <button
                          type="button"
                          className="my-photo-btn remove"
                          onClick={() => handleRemovePhoto(photo.id)}
                          disabled={deletingPhotoId === photo.id}
                        >
                          <FaTrash />
                          {deletingPhotoId === photo.id ? 'Retrait...' : 'Retirer'}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default MyPhotos;
